import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontFamily, pdfFontSize, pdfLetter, pdfSpacing } from '../theme';

export type ColumnAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<Row> {
  key: string;
  header: string;
  /** Fixed point width — if omitted, the column flexes to fill remaining space. */
  width?: number;
  /** Flex weight for columns without explicit width. Default 1. */
  flex?: number;
  align?: ColumnAlign;
  render: (row: Row, index: number) => React.ReactNode;
  mono?: boolean;
  /** Overrides for this column's heading — a smaller size so a long word fits a narrow column. */
  headerStyle?: { fontSize?: number; letterSpacing?: number; paddingHorizontal?: number };
}

/** A row that sums the table up: a label spanning every column before the
 *  first value, then the values by column key. A total closes the table
 *  under a dark rule; a balance (opening, closing) is a soft band that can
 *  also open it. */
export interface DataTableSummaryRow {
  label: React.ReactNode;
  values: Record<string, React.ReactNode>;
  kind?: 'total' | 'balance';
  /** Above the body (an opening balance). Default below. */
  before?: boolean;
}

interface Props<Row> {
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  /** Optional per-row style callback — e.g. an accent on rows inside the
   *  merit cut-line. */
  rowStyle?: (row: Row, index: number) => Record<string, unknown> | undefined;
  /** Full-width marker rendered before a row (e.g. the merit cut-line).
   *  Return null for no marker. */
  rowDivider?: (row: Row, index: number) => React.ReactNode;
  /** Zebra-stripe rows. Default off; enable where it reads better
   *  (long dense tables). */
  zebra?: boolean;
  compact?: boolean;
  /** Smaller text (7.5pt) — for wide registers that list many short fields. */
  small?: boolean;
  /** Rendered under the table — "N rows · generated on X" caption. */
  footerCaption?: string;
  summaryRows?: DataTableSummaryRow[];
  /** Shown in the body when there are no rows; the header and summary rows stay. */
  emptyText?: string;
}

const styles = StyleSheet.create({
  // Dark header — the backend's signature table treatment. `fixed` repeats
  // it at the top of every page the table flows onto.
  headerRow: {
    flexDirection: 'row',
    backgroundColor: pdfColors.textPrimary,
    borderRadius: 3,
  },
  headerCell: {
    paddingVertical: 6,
    paddingHorizontal: pdfSpacing.sm,
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    color: pdfColors.textOnDark,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'solid',
  },
  cell: {
    paddingVertical: 6,
    paddingHorizontal: pdfSpacing.sm,
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
  },
  cellCompact: {
    paddingVertical: 4.5,
  },
  cellSmall: {
    fontSize: pdfFontSize.xs,
    paddingHorizontal: 6,
  },
  cellMono: {
    fontFamily: pdfFontFamily.mono,
    fontSize: pdfFontSize.xs,
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: pdfColors.surfaceSoft,
    borderTopWidth: 1.2,
    borderTopColor: pdfColors.textPrimary,
    borderTopStyle: 'solid',
  },
  totalCell: { fontWeight: 700 },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fffbeb',
    borderTopWidth: 0.6,
    borderTopColor: pdfColors.warningBorder,
    borderTopStyle: 'solid',
    borderBottomWidth: 0.6,
    borderBottomColor: pdfColors.warningBorder,
    borderBottomStyle: 'solid',
  },
  balanceLabel: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    color: pdfColors.warning,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
  },
  empty: {
    paddingVertical: 18,
    textAlign: 'center',
    fontSize: pdfFontSize.xs,
    fontStyle: 'italic',
    color: pdfColors.textMuted,
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'solid',
  },
  footerCaption: {
    marginTop: 6,
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textMuted,
    textAlign: 'right',
  },
});

/**
 * A heading style that fits these labels into a column of `width`. Headings
 * are set in capitals and a word never breaks, so the longest word decides
 * the size — never larger than the standard heading.
 */
export const fitHeaderStyle = (labels: string[], width: number): NonNullable<DataTableColumn<unknown>['headerStyle']> => {
  const longestWord = Math.max(4, ...labels.flatMap(l => l.split(/\s+/).map(w => w.length)));
  return { fontSize: Math.min(pdfFontSize.xxs, (width - 7) / (longestWord * 0.64)), letterSpacing: 0.15, paddingHorizontal: 3 };
};

function widthStyle<R>(col: DataTableColumn<R>) {
  return col.width != null ? { width: col.width } : { flex: col.flex ?? 1 };
}

function cellStyleFor<R>(col: DataTableColumn<R>, compact: boolean, small = false): any[] {
  const base: any[] = [styles.cell, { textAlign: col.align || 'left' }];
  if (compact) base.push(styles.cellCompact);
  if (small) base.push(styles.cellSmall);
  if (col.mono) base.push(styles.cellMono);
  base.push(widthStyle(col));
  return base;
}

/** Generic data table. Dark header repeated on every page, hairline rows,
 *  optional zebra striping. Rows never split across a page break. */
function DataTable<Row>({ columns, rows, rowStyle, rowDivider, zebra, compact, small, footerCaption, summaryRows = [], emptyText }: Props<Row>) {
  const summary = (row: DataTableSummaryRow, key: number) => {
    const first = columns.findIndex(col => row.values[col.key] != null);
    const leading = columns.slice(0, Math.max(first, 0));
    const span = leading.some(col => col.width == null)
      ? { flex: 1 }
      : { width: leading.reduce((w, col) => w + (col.width ?? 0), 0) };
    const balance = row.kind === 'balance';
    return (
      <View key={key} style={balance ? styles.balanceRow : styles.totalRow} wrap={false}>
        {leading.length > 0 && (
          <View style={[...cellStyleFor(leading[0], !!compact, small), span, { textAlign: 'right' }]}>
            <Text style={balance ? styles.balanceLabel : styles.totalCell}>{row.label}</Text>
          </View>
        )}
        {columns.slice(leading.length).map((col) => (
          <View key={col.key} style={cellStyleFor(col, !!compact, small)}>
            {row.values[col.key] != null && <Text style={styles.totalCell}>{row.values[col.key]}</Text>}
          </View>
        ))}
      </View>
    );
  };
  return (
    <View>
      <View style={styles.headerRow} fixed>
        {columns.map((col) => (
          <Text key={col.key} style={[styles.headerCell, small ? { paddingHorizontal: 6 } : {}, { textAlign: col.align || 'left' }, widthStyle(col), col.headerStyle ?? {}]}>
            {col.header}
          </Text>
        ))}
      </View>
      {summaryRows.filter(r => r.before).map(summary)}
      {rows.length === 0 && emptyText && <Text style={styles.empty}>{emptyText}</Text>}
      {rows.map((row, i) => {
        const rowStyles: any[] = [styles.bodyRow];
        if (zebra && i % 2 === 1) rowStyles.push({ backgroundColor: pdfColors.surfaceSoft });
        const extra = rowStyle?.(row, i);
        if (extra) rowStyles.push(extra);
        return (
          <React.Fragment key={i}>
            {rowDivider?.(row, i)}
            <View style={rowStyles} wrap={false}>
              {columns.map((col) => (
                <View key={col.key} style={cellStyleFor(col, !!compact, small)}>
                  {renderCell(col.render(row, i))}
                </View>
              ))}
            </View>
          </React.Fragment>
        );
      })}
      {summaryRows.filter(r => !r.before).map(summary)}
      {footerCaption && <Text style={styles.footerCaption}>{footerCaption}</Text>}
    </View>
  );
}

function renderCell(node: React.ReactNode): React.ReactNode {
  if (node == null || node === '') return <Text>—</Text>;
  if (typeof node === 'string' || typeof node === 'number') return <Text>{String(node)}</Text>;
  return node;
}

export default DataTable;
